import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import useRoute from '@/Hooks/useRoute';
import { router } from '@inertiajs/react';
import useTypedPage from '@/Hooks/useTypedPage';
import { useFileContent } from './FileContentProvider';
import {
  EventSourceMessage,
  fetchEventSource,
} from '@microsoft/fetch-event-source';
import useTrackingHook from '@/Hooks/useTrackingHook';

type SSEProviderProps = {
  children: React.ReactNode;
};

export type EventType = {
  [key: string]: string;
};

type SSEContextType = {
  events: Map<string, any>;
  error?: string;
  assistant?: App.Data.AssistantBroadcastData;
  streaming: boolean;
  cancelled: boolean;
  cancelAssistant: () => void;
};

export const SSEContext = React.createContext<SSEContextType | undefined>({
  events: new Map(),
  error: undefined,
  streaming: false,
  cancelled: false,
  assistant: undefined,
  cancelAssistant: () => {},
});

export const useServerSentEvents = () => {
  const context = useContext(SSEContext);
  if (!context) {
    throw new Error(`useServerSentEvents must be used within an SSEProvider`);
  }
  return context;
};

const useForceUpdate = () => {
  const [, setTick] = useState(0);
  const update = useCallback(() => {
    setTick(tick => tick + 1);
  }, []);
  return update;
};

export const SSEProvider = ({ children }: SSEProviderProps) => {
  const { assistant } = useTypedPage<{
    assistant: App.Data.AssistantBroadcastData;
  }>().props;
  const route = useRoute();
  const forceUpdate = useForceUpdate();
  const { setState } = useFileContent();
  const [error, setError] = useState<string>();
  const [streaming, setStreaming] = useState<boolean>(false);
  const [cancelled, setCancelled] = useState<boolean>(false);
  const source = useRef<void | null>(null);
  const events = useRef<Map<string, any>>(new Map());
  const ctrl = useRef<AbortController>(new AbortController());
  const { track } = useTrackingHook();

  async function setupEventSource(assistantId: string) {
    if (!source.current) {
      source.current = await fetchEventSource(
        route('assistant.reply', assistantId),
        {
          credentials: 'include',
          async onopen() {
            events.current = new Map();
            setStreaming(true);
          },
          onmessage: handleEvent,
          onclose: resetStream,
          onerror(error) {
            console.error('EventSource event failure:', {
              assistant: assistantId,
              error,
            });
            setError(error.data);
            resetStream();
          },
          signal: ctrl.current.signal,
          openWhenHidden: true,
        }
      );
    }
  }

  function resetStream() {
    setStreaming(false);
    setState('idle');
  }

  function handleEvent(e: EventSourceMessage) {
    if (e.event === 'error') {
      setError(e.data);
      return;
    } else {
      events.current = new Map(events.current).set(e.event, e.data);
    }

    forceUpdate();
  }

  useEffect(() => {
    if (assistant) {
      setupEventSource(assistant.id);
    } else {
      ctrl.current.abort();
    }
  }, [assistant]);

  useEffect(() => {
    const faviconElement = document.querySelector("link[rel~='icon']");
    const url = streaming ? '/pending-ellipse.svg' : '/icon.svg';
    faviconElement?.setAttribute('href', url);
  }, [streaming]);

  function cancelAssistant() {
    ctrl.current.abort();
    resetStream();
    setCancelled(true);
    track('assistant:canceled', {
      assistant_id: assistant.id,
      file_id: assistant.file_id,
    });
    router.put(route('assistant.cancel', assistant.id));
  }

  return (
    <SSEContext.Provider
      value={{
        events: events.current,
        error,
        streaming,
        cancelled,
        assistant,
        cancelAssistant,
      }}
    >
      {children}
    </SSEContext.Provider>
  );
};

export default SSEProvider;
